import type { ReactElement } from 'react';
import type { DocumentSection, Proposal } from '@shared/types';
import { HeadphonesIcon, MapPinIcon, PlayIcon } from './icons';

/**
 * Cover hero for every momentum_v3 `product_divider` EXCEPT `paid_search`
 * (which keeps `SearchDeviceVisual` — see `ProductPages.tsx`).
 * `docs/momentum-v3-slides.md` product_divider; media-pairs-all-contract.md
 * "Cover heroes: `ChannelHero`". Inline HTML/CSS + inline SVG only, no PNG,
 * no screenshot — same reasoning as `SearchDeviceVisual`: a controlled
 * illustration of the channel's ad format, never another company's real UI.
 * `channelHeroModel` below is the pure data derivation ("agent supplies
 * content inputs, the renderer owns geometry"); this component only lays
 * the model out.
 *
 * Accuracy rule (media-pairs-all-contract.md "Cover heroes"): never invent
 * like counts, view counts, ratings, prices, ranks. Only "Sponsored" / "Ad"
 * labels and the copy the model carries.
 */

export type ChannelHeroKind =
  | 'social'
  | 'ctv'
  | 'targeted_display'
  | 'online_video'
  | 'streaming_audio'
  | 'email'
  | 'mobile_location';

export interface ChannelHeroModel {
  advertiserName: string;
  /** Hostname only, no protocol/www — blank when the advertiser has no domain on file. */
  domain: string;
  logoUrl: string | null;
  /** The on-screen ad line (media-pairs-all-contract.md `fields.heroLine`). */
  heroLine: string;
  /** Sibling plan `paths[0].conversionLabel`, else "Learn more". */
  ctaLabel: string;
  /** Divider `content.imageUrl` for the hero's image slot; null → tinted gradient. */
  imageUrl: string | null;
  /** Sibling plan `creative.format`, when set (e.g. ":30 spot", "15s pre-roll"). */
  format: string | null;
}

function hostnameOf(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return trimmed.replace(/^www\./, '').replace(/\/.*$/, '');
  }
}

/** The sibling `media_<productId>_plan` section's content, when one exists
 * (same lookup `SearchDeviceVisual.tsx`'s `planPathSibling` uses). */
function planContentSibling(proposal: Proposal, productId: string) {
  const sections = proposal.document?.sections ?? [];
  const sibling = sections.find(
    (candidate) =>
      candidate.productId === productId && (candidate.type === 'product_plan' || candidate.type === 'media_product'),
  );
  return sibling?.content ?? null;
}

/**
 * Pure derivation (no rendering) for `ChannelHero`. `heroLine` defaults to
 * the sibling plan's `creative.message`, else the divider headline
 * (media-pairs-all-contract.md "Optional cover field `fields.heroLine`").
 * `ctaLabel` defaults to the sibling plan's first `paths[0].conversionLabel`
 * (audience_plan), else "Learn more".
 *
 * Every string sourced from agent-authored copy (`fields.heroLine`,
 * `creative.message`, `content.headline`, `paths[0].conversionLabel`,
 * `creative.format`) can carry a live `{{binding}}` token, so the caller's
 * `useBindings()` resolver is threaded through here and applied before this
 * model reaches `ChannelHero`'s presentation components - the same rule
 * `StrategyPage` applies to `content.body`/`fields.support` (see
 * `StrategyPages.tsx`), just one level removed since this is a pure
 * function, not a component.
 */
export function channelHeroModel(
  proposal: Proposal,
  section: DocumentSection,
  resolve: (text: string) => string,
): ChannelHeroModel {
  const productId = section.productId ?? '';
  const advertiserName = proposal.advertiser.name?.trim() || 'This advertiser';
  const domain = hostnameOf(proposal.advertiser.domain);
  const logoUrl = proposal.advertiser.logoUrl ?? null;
  const planContent = planContentSibling(proposal, productId);
  const creative = planContent?.creative ?? null;
  const paths = planContent?.paths ?? null;

  const heroLineField = section.content.fields?.heroLine?.trim();
  const heroLine = resolve(heroLineField || creative?.message?.trim() || section.content.headline?.trim() || '');
  const ctaLabel = resolve(paths?.[0]?.conversionLabel?.trim() || 'Learn more');
  const imageUrl = section.content.imageUrl || null;
  const format = creative?.format?.trim() ? resolve(creative.format.trim()) : null;

  return { advertiserName, domain, logoUrl, heroLine, ctaLabel, imageUrl, format };
}

/* --------------------------------------------------------------------- */
/* Shared presentation pieces                                            */
/* --------------------------------------------------------------------- */

function Avatar({
  advertiserName,
  logoUrl,
  className,
}: {
  advertiserName: string;
  logoUrl: string | null;
  className?: string;
}): ReactElement {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className={`mv3-channelhero-avatar mv3-channelhero-avatar-img${className ? ` ${className}` : ''}`} />;
  }
  const letter = advertiserName.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className={`mv3-channelhero-avatar mv3-channelhero-avatar-glyph${className ? ` ${className}` : ''}`} aria-hidden="true">
      {letter}
    </span>
  );
}

/** The image slot every hero carries: `model.imageUrl` or a soft tinted gradient block. */
function ImageSlotBlock({ imageUrl, className }: { imageUrl: string | null; className?: string }): ReactElement {
  return (
    <div className={`mv3-channelhero-imageslot${className ? ` ${className}` : ''}`}>
      {imageUrl ? <img src={imageUrl} alt="" className="mv3-channelhero-imageslot-img" /> : null}
    </div>
  );
}

function CtaButton({ label }: { label: string }): ReactElement {
  return <span className="mv3-channelhero-cta">{label}</span>;
}

/* --------------------------------------------------------------------- */
/* Device chrome (near-black/white, same palette as SearchDeviceVisual)  */
/* --------------------------------------------------------------------- */

function LaptopFrame({ children, className }: { children: ReactElement; className?: string }): ReactElement {
  return (
    <div className={`mv3-channelhero-laptop${className ? ` ${className}` : ''}`}>
      <div className="mv3-channelhero-laptop-bezel">
        <div className="mv3-channelhero-laptop-page">{children}</div>
      </div>
      <div className="mv3-channelhero-laptop-base" aria-hidden="true" />
    </div>
  );
}

function PhoneFrame({
  children,
  className,
}: {
  children: ReactElement;
  className?: string;
}): ReactElement {
  return (
    <div className={`mv3-channelhero-phone${className ? ` ${className}` : ''}`}>
      <div className="mv3-channelhero-phone-bezel">
        <div className="mv3-channelhero-phone-notch" aria-hidden="true" />
        <div className="mv3-channelhero-phone-page">{children}</div>
      </div>
    </div>
  );
}

function TvFrame({ children, className }: { children: ReactElement; className?: string }): ReactElement {
  return (
    <div className={`mv3-channelhero-tv${className ? ` ${className}` : ''}`}>
      <div className="mv3-channelhero-tv-bezel">{children}</div>
      <div className="mv3-channelhero-tv-stand" aria-hidden="true" />
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Per-kind screen content                                               */
/* --------------------------------------------------------------------- */

function SocialHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-social">
      <PhoneFrame className="mv3-channelhero-phone-back">
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-reel">
            <ImageSlotBlock imageUrl={model.imageUrl} className="mv3-channelhero-reel-image" />
            <span className="mv3-channelhero-reel-handle">@{model.domain || model.advertiserName}</span>
          </div>
        </>
      </PhoneFrame>
      <PhoneFrame className="mv3-channelhero-phone-front">
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-feedpost">
            <div className="mv3-channelhero-feedpost-meta">
              <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} />
              <div className="mv3-channelhero-feedpost-metatext">
                <span className="mv3-channelhero-name">{model.advertiserName}</span>
                <span className="mv3-channelhero-sponsored">Sponsored</span>
              </div>
            </div>
            <ImageSlotBlock imageUrl={model.imageUrl} />
            <div className="mv3-channelhero-caption">{model.heroLine}</div>
            <CtaButton label={model.ctaLabel} />
          </div>
        </>
      </PhoneFrame>
    </div>
  );
}

function CtvHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-ctv">
      <TvFrame>
        <>
          <ImageSlotBlock imageUrl={model.imageUrl} className="mv3-channelhero-tv-image" />
          <div className="mv3-channelhero-lowerthird">
            <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} className="mv3-channelhero-avatar-sm" />
            <div className="mv3-channelhero-lowerthird-text">
              <span className="mv3-channelhero-name mv3-channelhero-name-light">{model.advertiserName}</span>
              <span className="mv3-channelhero-heroline-sm">{model.heroLine}</span>
            </div>
            <span className="mv3-channelhero-chip">Ad · {model.format || ':30'}</span>
          </div>
        </>
      </TvFrame>
    </div>
  );
}

function TargetedDisplayHero({ model }: { model: ChannelHeroModel }): ReactElement {
  const lockup = (
    <div className="mv3-channelhero-adunit-lockup">
      <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} className="mv3-channelhero-avatar-xs" />
      <span className="mv3-channelhero-name mv3-channelhero-name-xs">{model.advertiserName}</span>
    </div>
  );
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-display">
      <LaptopFrame>
        <>
          <div className="mv3-channelhero-leaderboard">
            {lockup}
            <span className="mv3-channelhero-heroline-xs">{model.heroLine}</span>
            <CtaButton label={model.ctaLabel} />
          </div>
          <div className="mv3-channelhero-article">
            <div className="mv3-channelhero-article-body">
              <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w80" />
              <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w95" />
              <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w60" />
              <ImageSlotBlock imageUrl={null} className="mv3-channelhero-article-image" />
              <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w90" />
              <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w70" />
            </div>
            <div className="mv3-channelhero-mrec">
              {lockup}
              <ImageSlotBlock imageUrl={model.imageUrl} className="mv3-channelhero-mrec-image" />
              <span className="mv3-channelhero-heroline-xs">{model.heroLine}</span>
              <CtaButton label={model.ctaLabel} />
            </div>
          </div>
        </>
      </LaptopFrame>
      <PhoneFrame className="mv3-channelhero-phone-overlap">
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-mobile-banner">
            {lockup}
            <span className="mv3-channelhero-heroline-xs">{model.heroLine}</span>
          </div>
          <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w80" />
          <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w60" />
        </>
      </PhoneFrame>
    </div>
  );
}

function OnlineVideoHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-video">
      <LaptopFrame>
        <div className="mv3-channelhero-videoplayer">
          <ImageSlotBlock imageUrl={model.imageUrl} className="mv3-channelhero-videoplayer-image" />
          <span className="mv3-channelhero-chip mv3-channelhero-chip-video">Ad · {model.format || '0:15'}</span>
          <span className="mv3-channelhero-skip">Skip ad</span>
          <div className="mv3-channelhero-videoplayer-bottom">
            <span className="mv3-channelhero-name mv3-channelhero-name-light">{model.advertiserName}</span>
            <div className="mv3-channelhero-playbar">
              <PlayIcon size={16} className="mv3-channelhero-playicon" />
              <span className="mv3-channelhero-playbar-track">
                <span className="mv3-channelhero-playbar-fill" />
              </span>
            </div>
          </div>
        </div>
      </LaptopFrame>
    </div>
  );
}

function StreamingAudioHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-audio">
      <PhoneFrame>
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-nowplaying">
            <span className="mv3-channelhero-advertisement-label">Advertisement</span>
            <div className="mv3-channelhero-coverart">
              {model.logoUrl ? (
                <img src={model.logoUrl} alt="" className="mv3-channelhero-coverart-img" />
              ) : (
                <span className="mv3-channelhero-coverart-glyph" aria-hidden="true">
                  {model.advertiserName.trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
            </div>
            <span className="mv3-channelhero-tracktitle">{model.heroLine}</span>
            <span className="mv3-channelhero-artist">{model.advertiserName}</span>
            <span className="mv3-channelhero-progress">
              <span className="mv3-channelhero-progress-fill" />
            </span>
            <div className="mv3-channelhero-transport">
              <HeadphonesIcon size={20} />
            </div>
          </div>
        </>
      </PhoneFrame>
    </div>
  );
}

function EmailHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-email">
      <LaptopFrame className="mv3-channelhero-laptop-back">
        <div className="mv3-channelhero-emailopen">
          <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} />
          <span className="mv3-channelhero-heroline-sm">{model.heroLine}</span>
          <CtaButton label={model.ctaLabel} />
        </div>
      </LaptopFrame>
      <PhoneFrame className="mv3-channelhero-phone-front">
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-inbox">
            <div className="mv3-channelhero-inbox-row mv3-channelhero-inbox-row-active">
              <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} className="mv3-channelhero-avatar-xs" />
              <div className="mv3-channelhero-inbox-rowtext">
                <span className="mv3-channelhero-name mv3-channelhero-name-xs">{model.advertiserName}</span>
                <span className="mv3-channelhero-inbox-subject">{model.heroLine}</span>
              </div>
              <span className="mv3-channelhero-sponsored">Sponsored</span>
            </div>
            <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w80" />
            <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w60" />
            <span className="mv3-channelhero-textbar mv3-channelhero-textbar-w90" />
          </div>
        </>
      </PhoneFrame>
    </div>
  );
}

function MobileLocationHero({ model }: { model: ChannelHeroModel }): ReactElement {
  return (
    <div className="mv3-channelhero-devices mv3-channelhero-devices-location">
      <PhoneFrame>
        <>
          <div className="mv3-channelhero-phone-status" aria-hidden="true" />
          <div className="mv3-channelhero-map">
            <svg viewBox="0 0 220 260" className="mv3-channelhero-map-svg" role="img" aria-hidden="true">
              <rect x="0" y="0" width="220" height="260" fill="#eef0ec" />
              <path d="M0 60h220M0 130h220M0 195h220" stroke="#d7dad2" strokeWidth="4" />
              <path d="M45 0v260M150 0v260" stroke="#d7dad2" strokeWidth="4" />
              <circle cx="72" cy="92" r="4.5" fill="#8a8d88" />
              <circle cx="130" cy="160" r="4.5" fill="#8a8d88" />
              <circle cx="100" cy="130" r="8" fill="var(--mv3-mint)" />
            </svg>
            <MapPinIcon size={26} className="mv3-channelhero-map-pin" />
          </div>
          <div className="mv3-channelhero-bottomsheet">
            <Avatar advertiserName={model.advertiserName} logoUrl={model.logoUrl} className="mv3-channelhero-avatar-xs" />
            <div className="mv3-channelhero-bottomsheet-text">
              <span className="mv3-channelhero-name mv3-channelhero-name-xs">{model.advertiserName}</span>
              <span className="mv3-channelhero-heroline-xs">{model.heroLine}</span>
            </div>
            <CtaButton label={model.ctaLabel} />
          </div>
        </>
      </PhoneFrame>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Entry point                                                           */
/* --------------------------------------------------------------------- */

const HERO_BY_KIND: Record<ChannelHeroKind, (props: { model: ChannelHeroModel }) => ReactElement> = {
  social: SocialHero,
  ctv: CtvHero,
  targeted_display: TargetedDisplayHero,
  online_video: OnlineVideoHero,
  streaming_audio: StreamingAudioHero,
  email: EmailHero,
  mobile_location: MobileLocationHero,
};

export function ChannelHero({
  kind,
  model,
  className,
}: {
  kind: ChannelHeroKind;
  model: ChannelHeroModel;
  className?: string;
}): ReactElement {
  const Hero = HERO_BY_KIND[kind];
  return <div className={`mv3-channelhero${className ? ` ${className}` : ''}`}>{Hero({ model })}</div>;
}
